import { CSSProperties, Theme, makeStyles, useTheme } from '@forgeglobal/design-framework'
import { ExpansionTableHeader } from './ExpansionTableHeader'
import { SortDirection } from 'graphql/bindings/AdminBindings'
import { VariableSizeList } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import React, { ReactNode, useRef, useState } from 'react'
import ResizeSensor from 'resize-sensor'

export type ColumnDef = {
  name: string
  text: string
  textSecondary?: string
  width: number
  sortable?: boolean
}

export interface ColumnSortProps {
  field: string
  direction: SortDirection
}

export interface ExpansionTableRowProps<T> {
  index: number
  style: CSSProperties
  columnDefs: Array<ColumnDef>
  data: T
  expand: () => void
  expanded?: boolean
}

type ExpansionTableProps<T> = {
  columnDefs: Array<ColumnDef>
  sort?: ColumnSortProps
  onClickSort?: (column: string) => void
  tableHeight?: number
  rowHeights: {
    collapsed: number
    expanded?: number
  }
  data: Array<T>
  loadMore: (fromIndex: number, toIndex: number) => Promise<void>
  component: React.FC<ExpansionTableRowProps<T>>
  displayIfNone: ReactNode
  rowCount: number
}

const useStyles = makeStyles((theme: Theme) => ({
  expansionTableRoot: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  expansionTableBody: {
    display: 'flex',
    flexGrow: 1,
  },
}))

export function ExpansionTable<T>(props: ExpansionTableProps<T>) {
  const defaultHeight = 6 * props.rowHeights.collapsed

  const heightRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<VariableSizeList>(null)

  const [indexExpanded, setIndexExpanded] = useState<number>(-1)
  const [clientHeight, setClientHeight] = useState<number>(defaultHeight)

  const theme = useTheme()
  const classes = useStyles()

  const updateHeight = () => {
    const clientHeight = heightRef && heightRef.current ? heightRef.current.clientHeight : defaultHeight
    setClientHeight(clientHeight)
  }

  const rowHeight = (index: number) => {
    let height = props.rowHeights.collapsed
    if (index === indexExpanded) {
      height = props.rowHeights.expanded || height
    }
    return height + theme.spacing(1)
  }

  const clickRow = (index: number) => {
    const resetAfter = indexExpanded !== -1 ? Math.min(indexExpanded, index) : index
    setIndexExpanded(index === indexExpanded ? -1 : index)
    listRef.current && listRef.current.resetAfterIndex(resetAfter, true)
  }

  const Row: React.FC<{ index: any; style: any }> | null = ({ index, style }) => {
    const expand = () => {
      clickRow(index)
    }

    const itemData = props.data[index as number]
    return itemData ? (
      <props.component
        index={index}
        style={style}
        columnDefs={props.columnDefs}
        data={itemData}
        expand={expand}
        expanded={index === indexExpanded}
      />
    ) : null
  }

  return (
    <div className={classes.expansionTableRoot}>
      <ExpansionTableHeader
        columnDefs={props.columnDefs}
        sort={props.sort}
        onClickSort={props.onClickSort && props.onClickSort}
        hasIconPadding={props.rowHeights.expanded !== undefined}
      />
      <div
        className={classes.expansionTableBody}
        ref={rf => {
          heightRef.current = rf
          rf && new ResizeSensor(rf, updateHeight)
        }}>
        <div style={{ flexGrow: 1 }}>
          <InfiniteLoader
            isItemLoaded={(index: number) => props.data.length > index}
            itemCount={props.rowCount}
            loadMoreItems={props.loadMore}>
            {({ onItemsRendered }) => (
              <VariableSizeList
                ref={listRef}
                height={props.tableHeight || clientHeight}
                width="100%"
                itemCount={props.rowCount}
                onItemsRendered={onItemsRendered}
                itemSize={rowHeight}>
                {Row}
              </VariableSizeList>
            )}
          </InfiniteLoader>
        </div>
      </div>
    </div>
  )
}
